import axios, { AxiosRequestConfig } from 'axios';
import { AuthService } from '../../../services/auth.service';
import store from '../../../store';

export const baseDomain = process.env.VUE_APP_BACKEND;

export const baseURL = `${baseDomain}/web/api/`;

export const Http = axios.create({
    baseURL,
    headers: { 'Content-Type': 'application/json' }
});

export const setAuthorizationHeader = (token: string | null = null) => {
    if (token) {
        Http.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
        delete Http.defaults.headers.common.Authorization;
    }
};

export const getParams = (pairs) =>
    pairs.reduce((params, pair) => {
        if (pair.value) {
            return { ...params, [pair.key]: pair.value };
        }
        return params;
    }, {});

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

// Request interceptor - add access token to all requests
Http.interceptors.request.use(
    async (config: any) => {
        const token = store.getters['auth/accessToken'];
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    err => {
        return Promise.reject(err);
    }
);

// Response interceptor - handle 401 errors and refresh token
Http.interceptors.response.use(
    response => {
        return response;
    },
    async (error: any) => {
        const originalRequest: AxiosRequestConfig & { _retry?: boolean } = error.config;

        // If error is 401 and we haven't retried yet
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                // If already refreshing, queue this request
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then(token => {
                        if (originalRequest.headers) {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                        }
                        return Http(originalRequest);
                    })
                    .catch(err => {
                        return Promise.reject(err);
                    });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            const refreshToken = store.getters['auth/refreshToken'];

            if (!refreshToken) {
                // No refresh token available, logout
                store.dispatch('auth/logout');
                processQueue(error, null);
                window.location.href = '/login';
                return Promise.reject(error);
            }

            try {
                const response = await AuthService.refreshToken(refreshToken);
                const newAccessToken = response.access_token;
                const expiresIn = response.expires_in;

                // Update the store with new token
                store.dispatch('auth/updateToken', {
                    accessToken: newAccessToken,
                    expiresIn
                });

                // Update authorization header
                setAuthorizationHeader(newAccessToken);

                // Process queued requests
                processQueue(null, newAccessToken);

                // Retry original request
                if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                }
                return Http(originalRequest);
            } catch (refreshError) {
                // Refresh token failed, logout user
                processQueue(refreshError, null);
                store.dispatch('auth/logout');
                window.location.href = '/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

export default Http;
